/**
 * DateTimeField — responsive date+time control for Ant Design forms.
 *
 * Mobile/tablet (< md): separate full-width native date + time inputs.
 * Combined datetime-local still opens a wide calendar+time popup in Chrome
 * that gets clipped on small screens; split controls use OS pickers that fit.
 *
 * Desktop: Ant Design DatePicker with showTime.
 *
 * Value contract: dayjs | null (same as Ant DatePicker) so Form.Item callers
 * can keep using values.departureDate.toISOString().
 */

import React from "react";
import { DatePicker, Input } from "antd";
import styled from "styled-components";
import dayjs from "dayjs";
import { useDeviceType } from "../../features/leads/hooks/useDeviceType.js";

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";
const DATETIME_LOCAL_FORMAT = "YYYY-MM-DDTHH:mm";
const DISPLAY_FORMAT = "YYYY-MM-DD HH:mm";

const MobileFieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const NativeInput = styled(Input)`
  width: 100%;
  min-height: 44px;
  font-size: 16px; /* Prevents iOS Safari zoom on focus */

  &.ant-input {
    min-height: 44px;
    font-size: 16px;
  }
`;

const DesktopDatePicker = styled(DatePicker)`
  width: 100%;
`;

/**
 * @param {import("dayjs").Dayjs | string | Date | null | undefined} value
 * @returns {string}
 */
export const toDatetimeLocalValue = (value) => {
  if (!value) return "";
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  return parsed.isValid() ? parsed.format(DATETIME_LOCAL_FORMAT) : "";
};

/**
 * @param {string} stringValue
 * @returns {import("dayjs").Dayjs | null}
 */
export const fromDatetimeLocalValue = (stringValue) => {
  if (!stringValue) return null;
  const parsed = dayjs(stringValue);
  return parsed.isValid() ? parsed : null;
};

/**
 * @param {import("dayjs").Dayjs | string | Date | null | undefined} value
 * @returns {{ date: string, time: string }}
 */
export const toSplitDateTimeValues = (value) => {
  if (!value) return { date: "", time: "" };
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  if (!parsed.isValid()) return { date: "", time: "" };
  return {
    date: parsed.format(DATE_FORMAT),
    time: parsed.format(TIME_FORMAT),
  };
};

/**
 * @param {string} date
 * @param {string} time
 * @returns {import("dayjs").Dayjs | null}
 */
export const fromSplitDateTimeValues = (date, time) => {
  if (!date) return null;
  const parsed = dayjs(`${date}T${time || "00:00"}`);
  return parsed.isValid() ? parsed : null;
};

/**
 * @param {object} props
 * @param {import("dayjs").Dayjs | null} [props.value]
 * @param {(value: import("dayjs").Dayjs | null) => void} [props.onChange]
 * @param {boolean} [props.disablePast] - Disallow dates before today (start of day)
 * @param {boolean} [props.disabled]
 * @param {string} [props.placeholder]
 * @param {string} [props.id]
 * @param {string} [props["aria-label"]]
 * @param {boolean} [props.forceNative] - Force native inputs (useful in tests)
 * @param {boolean} [props.forceDesktop] - Force Ant DatePicker (useful in tests)
 */
const DateTimeField = ({
  value,
  onChange,
  disablePast = false,
  disabled = false,
  placeholder = "Select date and time",
  id,
  "aria-label": ariaLabel = "Select date and time",
  forceNative = false,
  forceDesktop = false,
  ...rest
}) => {
  const { isMobile } = useDeviceType();
  const useNative = forceNative || (isMobile && !forceDesktop);

  const handleDesktopChange = (nextValue) => {
    if (!onChange) return;
    onChange(nextValue ?? null);
  };

  // Match Ant disabledDate: block picking past days, but do not set min below
  // an existing value (Edit Trip may load a past departure that must still submit).
  const startOfToday = dayjs().startOf("day");
  const currentValue = value
    ? dayjs.isDayjs(value)
      ? value
      : dayjs(value)
    : null;
  const minBound =
    disablePast && currentValue?.isValid() && currentValue.isBefore(startOfToday)
      ? currentValue.startOf("day")
      : disablePast
        ? startOfToday
        : null;
  const minDate = minBound ? minBound.format(DATE_FORMAT) : undefined;

  if (useNative) {
    const { date, time } = toSplitDateTimeValues(value);

    const emitSplit = (nextDate, nextTime) => {
      if (!onChange) return;
      onChange(fromSplitDateTimeValues(nextDate, nextTime));
    };

    return (
      <MobileFieldStack data-testid="datetime-field-mobile">
        <NativeInput
          id={id}
          type="date"
          value={date}
          onChange={(event) => emitSplit(event.target.value, time || "00:00")}
          onFocus={(event) => {
            event.currentTarget.scrollIntoView({
              block: "center",
              behavior: "smooth",
            });
          }}
          min={minDate}
          disabled={disabled}
          aria-label={`${ariaLabel} date`}
          {...rest}
        />
        <NativeInput
          id={id ? `${id}-time` : undefined}
          type="time"
          value={time}
          onChange={(event) =>
            emitSplit(date || dayjs().format(DATE_FORMAT), event.target.value)
          }
          onFocus={(event) => {
            event.currentTarget.scrollIntoView({
              block: "center",
              behavior: "smooth",
            });
          }}
          disabled={disabled}
          aria-label={`${ariaLabel} time`}
        />
      </MobileFieldStack>
    );
  }

  return (
    <DesktopDatePicker
      id={id}
      showTime
      format={DISPLAY_FORMAT}
      value={value ?? null}
      onChange={handleDesktopChange}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      inputReadOnly
      disabledDate={
        disablePast
          ? (current) => current && current < dayjs().startOf("day")
          : undefined
      }
      {...rest}
    />
  );
};

export default DateTimeField;
